import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getMe } from '../widget-api/auth-me';
import { postSelectTrainerBranch } from '../widget-api/trainer-branch';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { clearEmbedSession, clearGlobalEmbedSession, getEmbedSession, setEmbedSession } from '../widget-lib/embed-session-store';
import { PATH } from '../widget-lib/path-routes';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { BlockLoader, LoaderRing } from '../widget-ui/block-loader';

export default function TrainerBranchSelectPage() {
  useWidgetSeo(
    'Выбор филиала | Кабинет тренера',
    'Выбор рабочего филиала в кабинете тренера.'
  );
  const navigate = useNavigate();
  const { orgSlug, accessToken, me, meLoading } = useEmbedBootstrap();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const choose = useCallback(async (branchId: number) => {
    if (!accessToken) return;
    const slug = orgSlug || me?.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_slug || null;
    if (!slug) {
      setActionError('Не удалось определить символьный код организации.');
      return;
    }
    setBusyId(branchId);
    setActionError(null);
    try {
      const currentSession = getEmbedSession(slug);
      await postSelectTrainerBranch(branchId, accessToken);
      const fresh = await getMe(accessToken);
      if (fresh.organization_id == null) {
        setActionError('Не удалось обновить контекст организации.');
        return;
      }
      setEmbedSession(slug, {
        accessToken: accessToken,
        organizationId: fresh.organization_id,
        embedMode: 'trainer',
        refreshToken: currentSession?.refreshToken
      });
      const search = new URLSearchParams(window.location.search);
      search.set('org_slug', slug);
      navigate(`${PATH.trainer}?${search.toString()}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearEmbedSession(slug);
        clearGlobalEmbedSession();
        window.location.reload();
        return;
      }
      if (e instanceof ApiError) {
        setActionError(e.detail);
      } else {
        setActionError(e instanceof Error ? e.message : 'Ошибка');
      }
    } finally {
      setBusyId(null);
    }
  }, [orgSlug, me, accessToken, navigate]);

  useEffect(() => {
    if (!me || meLoading) return;
    const placements = me.trainer_placements ?? [];
    if (placements.length === 1) {
      void choose(placements[0].branch_id);
    } else if (placements.length === 0) {
      const slug = orgSlug || me.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_slug || null;
      const search = new URLSearchParams(window.location.search);
      if (slug) search.set('org_slug', slug);
      navigate(`${PATH.trainer}?${search.toString()}`, { replace: true });
    }
  }, [me, meLoading, navigate, orgSlug, choose]);

  if (meLoading || !me) {
    return (
      <div className="w-full min-w-0">
        <BlockLoader label="Загрузка профиля…" />
      </div>
    );
  }

  const placements = me.trainer_placements ?? [];
  if (placements.length <= 1) {
    return (
      <div className="w-full min-w-0">
        <BlockLoader label="Переход к расписанию…" />
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">Выберите филиал</h1>
        <p className="text-sm text-[var(--text-muted)]">
          У вас несколько активных привязок. Пожалуйста, укажите филиал, в котором вы сегодня ведёте смену, для настройки расписания.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {placements.map((p) => {
          const isBusy = busyId === p.branch_id;
          return (
            <button
              key={p.id}
              type="button"
              disabled={busyId !== null}
              onClick={() => void choose(p.branch_id)}
              className="group relative flex items-start gap-4 p-5 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] text-left shadow-[var(--shadow-card)] transition-all duration-200 hover:border-[var(--accent)] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] w-full cursor-pointer"
            >
              {/* Иконка здания/филиала */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--bg-soft)] text-[var(--accent)] transition-colors group-hover:bg-[var(--accent-bg)] group-hover:text-[var(--accent-fg)]">
                {isBusy ? (
                  <LoaderRing size="sm" hole="soft" className="!h-5 !w-5" />
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-10.5h16.5M2.25 9h19.5M4.5 19.5V6.25c0-.69.56-1.25 1.25-1.25h12.5c.69 0 1.25.56 1.25 1.25v13.25" />
                  </svg>
                )}
              </div>

              {/* Информация о филиале */}
              <div className="flex-1 min-w-0 pr-6">
                <span className="block text-base font-bold leading-snug text-[var(--text)] group-hover:text-[var(--accent)] transition-colors truncate">
                  {p.branch_name}
                </span>
                <span className="block text-[13px] text-[var(--text-muted)] mt-0.5">
                  {isBusy ? 'Подключаемся…' : 'Перейти в кабинет'}
                </span>
              </div>

              {/* Стрелочка перехода */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] transition-all duration-200 group-hover:text-[var(--accent)] group-hover:translate-x-1" aria-hidden="true">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>

      {actionError ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          {actionError}
        </EmbedMessageBlock>
      ) : null}
    </div>
  );
}
